import React, { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import { Card, Grid, Typography, Box, Paper } from '@mui/material';
import { db } from './auth/firebaseConfig';
import { collection, query, where, getDocs } from 'firebase/firestore';

// Currency symbol mapping
const currencySymbols = {
  'INR': '₹',
  'USD': '$',
  'EUR': '€',
  'GBP': '£',
  'SGD': 'S$',
  'JPY': '¥'
};

const CRMMetricsCharts = ({ boardId, projectId }) => {
  const [crmMetrics, setCrmMetrics] = useState({
    totalLeads: 0,
    leadsByStage: {},
    conversionRate: 0,
    averageDealValue: 0,
    averageDealValueCurrency: 'USD',
    leadTime: [],
    cycleTime: []
  });

  useEffect(() => {
    const fetchCRMMetrics = async () => {
      try {
        // Fetch cards for the board
        let cardsQuery;
        if (boardId === 'ALL_BOARDS') {
          // If ALL_BOARDS, fetch all cards for the project
          cardsQuery = query(
            collection(db, 'cards'),
            where('projectId', '==', projectId)
          );
        } else {
          cardsQuery = query(
            collection(db, 'cards'),
            where('boardId', '==', boardId)
          );
        }
        const cardsSnapshot = await getDocs(cardsQuery);
        const cards = cardsSnapshot.docs.map(doc => doc.data());

        // Calculate Total Leads (cards not in Won or Lost columns)
        const totalLeads = cards.filter(card => 
          card.cardStatus !== 'Won' && card.cardStatus !== 'Lost'
        ).length;

        // Calculate Leads by Column (group by cardStatus - which represents column names)
        const leadsByStage = {};
        cards.forEach(card => {
          const columnName = card.cardStatus || 'Unknown';
          // Group cards by their column (cardStatus contains the column name)
          leadsByStage[columnName] = (leadsByStage[columnName] || 0) + 1;
        });

        // Calculate Conversion Rate: (Number of Won deals / Total leads) × 100
        // Use all cards for conversion rate calculation, not just active leads
        const allCardsCount = cards.length;
        const wonDeals = cards.filter(card => card.cardStatus === 'Won').length;
        const conversionRate = allCardsCount > 0 ? ((wonDeals / allCardsCount) * 100).toFixed(2) : 0;

        // Calculate Average Deal Value: Mean of deal_value for all closed (Won) deals
        const wonCards = cards.filter(card => card.cardStatus === 'Won');
        
        // Group deals by currency for accurate averaging
        const dealsByCurrency = {};
        wonCards.forEach(card => {
          const dealValue = card.templateData?.deal_value || card.templateData?.dealValue || 0;
          const currency = card.templateData?.deal_value_currency || 'USD';
          if (dealValue > 0) {
            if (!dealsByCurrency[currency]) {
              dealsByCurrency[currency] = [];
            }
            const value = typeof dealValue === 'number' ? dealValue : parseFloat(dealValue) || 0;
            if (value > 0) {
              dealsByCurrency[currency].push(value);
            }
          }
        });
        
        // Calculate average for the most common currency (or first available)
        let averageDealValue = 0;
        let dominantCurrency = 'USD';
        let maxCount = 0;
        
        Object.keys(dealsByCurrency).forEach(currency => {
          const values = dealsByCurrency[currency];
          if (values.length > maxCount) {
            maxCount = values.length;
            dominantCurrency = currency;
            averageDealValue = values.reduce((sum, val) => sum + val, 0) / values.length;
          }
        });

        // Helper to parse custom date format
        const parseCustomDate = (dateStr) => {
          if (!dateStr) return null;
          const [datePart, timePart] = (dateStr || '').split(',').map(s => s?.trim());
          if (!datePart || !timePart) return null;
          const [day, monthStr, year] = datePart.split('-').map(s => s?.trim());
          const months = {
            Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
            Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11
          };
          const month = months[monthStr];
          if (month === undefined) return null;
          let [time, ampm] = timePart.split(' ').map(s => s?.trim());
          let [hours, minutes, seconds] = time.split(':').map(s => s?.trim());
          hours = parseInt(hours, 10);
          if (ampm && ampm.toLowerCase() === 'pm' && hours !== 12) hours += 12;
          if (ampm && ampm.toLowerCase() === 'am' && hours === 12) hours = 0;
          return new Date(year, month, parseInt(day, 10), hours, parseInt(minutes, 10), parseInt(seconds, 10));
        };

        // Lead Time (time from creation to completion - for Won/Lost deals)
        const leadTimeData = cards
          .filter(card => (card.cardStatus === 'Won' || card.cardStatus === 'Lost') && card.cardCreationDate && card.cardUpdatedOn)
          .map(card => {
            const creationDate = parseCustomDate(card.cardCreationDate) || new Date(card.cardCreationDate);
            const completionDate = new Date(card.cardUpdatedOn);
            const value = (completionDate - creationDate) / (1000 * 60 * 60 * 24); // Convert to days
            return {
              date: completionDate.getFullYear() + '-' + String(completionDate.getMonth() + 1).padStart(2, '0') + '-' + String(completionDate.getDate()).padStart(2, '0'),
              value
            };
          })
          .sort((a, b) => new Date(a.date) - new Date(b.date));

        // Cycle Time (time from start to completion - for Won/Lost deals)
        const cycleTimeData = cards
          .filter(card => (card.cardStatus === 'Won' || card.cardStatus === 'Lost') && (card.assignDate || card.cardCreationDate) && card.cardUpdatedOn)
          .map(card => {
            const startDate = parseCustomDate(card.assignDate || card.cardCreationDate) || new Date(card.assignDate || card.cardCreationDate);
            const completionDate = new Date(card.cardUpdatedOn);
            const value = (completionDate - startDate) / (1000 * 60 * 60 * 24); // Convert to days
            return {
              date: completionDate.getFullYear() + '-' + String(completionDate.getMonth() + 1).padStart(2, '0') + '-' + String(completionDate.getDate()).padStart(2, '0'),
              value
            };
          })
          .sort((a, b) => new Date(a.date) - new Date(b.date));

        setCrmMetrics({
          totalLeads,
          leadsByStage,
          conversionRate: parseFloat(conversionRate),
          averageDealValue,
          averageDealValueCurrency: dominantCurrency,
          leadTime: leadTimeData,
          cycleTime: cycleTimeData
        });
      } catch (error) {
        console.error('Error fetching CRM metrics:', error);
      }
    };

    if (boardId && projectId) {
      fetchCRMMetrics();
    }
  }, [boardId, projectId]);


  const getLeadTimeOptions = () => ({
    title: {
      text: 'Lead Time (Days)',
      left: 'center'
    },
    tooltip: {
      trigger: 'axis',
      formatter: '{b}: {c} days'
    },
    xAxis: {
      type: 'category',
      data: crmMetrics.leadTime.map(item => item.date)
    },
    yAxis: {
      type: 'value',
      name: 'Days'
    },
    series: [{
      data: crmMetrics.leadTime.map(item => item.value),
      type: 'line',
      smooth: true,
      areaStyle: {
        opacity: 0.3
      }
    }]
  });

  const getCycleTimeOptions = () => ({
    title: {
      text: 'Cycle Time (Days)',
      left: 'center'
    },
    tooltip: {
      trigger: 'axis',
      formatter: '{b}: {c} days'
    },
    xAxis: {
      type: 'category',
      data: crmMetrics.cycleTime.map(item => item.date)
    },
    yAxis: {
      type: 'value',
      name: 'Days'
    },
    series: [{
      data: crmMetrics.cycleTime.map(item => item.value),
      type: 'line',
      smooth: true,
      areaStyle: {
        opacity: 0.3
      }
    }]
  });

  const getPieChartOptions = () => {
    // Define the standard CRM column order for better visualization
    const crmColumnOrder = [
      'Prospecting',
      'Qualified',
      'Proposal Sent',
      'Negotiation',
      'Won',
      'Lost',
      'Withdrawn'
    ];

    // Get all columns from the data, grouped by column name (cardStatus)
    const leadsByColumn = crmMetrics.leadsByStage || {};
    
    // Create pie chart data, sorted by CRM column order
    const pieData = Object.entries(leadsByColumn)
      .map(([columnName, count]) => ({
        name: columnName,
        value: count
      }))
      .sort((a, b) => {
        // Sort by CRM column order, then alphabetically for any custom columns
        const indexA = crmColumnOrder.indexOf(a.name);
        const indexB = crmColumnOrder.indexOf(b.name);
        
        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB;
        } else if (indexA !== -1) {
          return -1;
        } else if (indexB !== -1) {
          return 1;
        } else {
          return a.name.localeCompare(b.name);
        }
      });

    return {
      title: {
        text: 'Cards Grouped by Stage',
        left: 'center',
        textStyle: {
          fontSize: 18,
          fontWeight: 'bold'
        }
      },
      tooltip: {
        trigger: 'item',
        formatter: function(params) {
          return `${params.name}<br/>Cards: ${params.value} (${params.percent}%)`;
        }
      },
      legend: {
        orient: 'vertical',
        left: 'left',
        itemGap: 10
      },
      series: [{
        name: 'Cards',
        type: 'pie',
        radius: '50%', // Simple pie chart
        center: ['60%', '50%'],
        avoidLabelOverlap: true,
        itemStyle: {
          borderRadius: 5,
          borderColor: '#fff',
          borderWidth: 2
        },
        label: {
          show: true,
          formatter: '{b}\n({c})',
          fontSize: 12
        },
        labelLine: {
          show: true
        },
        data: pieData,
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)',
            scale: true,
            scaleSize: 10
          },
          label: {
            fontSize: 14,
            fontWeight: 'bold'
          }
        }
      }]
    };
  };

  return (
    <Box>
      {/* Summary Stats */}
      <Grid 
  container 
  spacing={3} 
  sx={{ mb: 4 }} 
  justifyContent="space-between"
>
  <Grid item xs={12} sm={4} md={4}>
    <Paper elevation={6} sx={{
      padding: 2,
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      borderRadius: 2,
      color: '#fff',
      textAlign: 'center'
    }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', marginBottom: 1 }}>
        Total Leads
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
        {crmMetrics.totalLeads}
      </Typography>
    </Paper>
  </Grid>

  <Grid item xs={12} sm={4} md={4}>
    <Paper elevation={6} sx={{
      padding: 2,
      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      borderRadius: 2,
      color: '#fff',
      textAlign: 'center'
    }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', marginBottom: 1 }}>
        Conversion Rate
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
        {crmMetrics.conversionRate}%
      </Typography>
    </Paper>
  </Grid>

  {/* <Grid item xs={12} sm={4} md={4}>
    <Paper elevation={6} sx={{
      padding: 2,
      background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      borderRadius: 2,
      color: '#fff',
      textAlign: 'center'
    }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', marginBottom: 1 }}>
        Average Deal Value
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
        {currencySymbols[crmMetrics.averageDealValueCurrency] || '$'}{crmMetrics.averageDealValue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
      </Typography>
    </Paper>
  </Grid> */}
</Grid>


      {/* Lead Time and Cycle Time Charts - Hidden for CRM Template */}
      {/* <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2, height: '100%' }}>
            <ReactECharts
              option={getLeadTimeOptions()}
              style={{ height: '300px' }}
            />
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2, height: '100%' }}>
            <ReactECharts
              option={getCycleTimeOptions()}
              style={{ height: '300px' }}
            />
          </Card>
        </Grid>
      </Grid> */}

      {/* Pie Chart - Leads by Stage */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card sx={{ p: 3, borderRadius: 3, boxShadow: 3, backgroundColor: '#fff' }}>
            <Typography
              variant="h5"
              component="div"
              sx={{
                marginBottom: 2,
                fontWeight: 600,
                textAlign: 'center',
                color: '#333',
              }}
            >
              Leads by Stage
            </Typography>
            <ReactECharts
              option={getPieChartOptions()}
              style={{ height: '400px', width: '100%' }}
            />
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CRMMetricsCharts;

